import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { MatchingAppointment, MatchingData, MatchingUser, SelectedSuggestion, Suggestion } from './move-order-suggestions.types';
import moment from 'moment';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { RoomModel } from 'projects/core/src/lib/models/room.model';
import { ROWS_PER_PAGE } from 'projects/admin/src/app/shared/constants';
import { AppointmentDataDaytype } from 'projects/core/src/lib/models/appointment.model';
import { DateRange, extendMoment } from 'moment-range';

const { range } = extendMoment(moment);
const MIN_DURATION = 120;

interface TimePeriod {
  starts_at: string;
  ends_at: string;
}

@Component({
  selector: 'app-move-order-suggestions',
  templateUrl: './move-order-suggestions.component.html',
  styleUrls: ['./move-order-suggestions.component.scss']
})
export class MoveOrderSuggestionsComponent implements OnInit, OnChanges {

  @Input() visible = false;
  @Input() order: AcModel = null;
  @Input() options: { [key: string]: Array<any> };
  @Input() locations: Array<LocationModel> = [];
  @Input() movingDate: Date | null = null;
  @Input() movingLocation: LocationModel | null = null;

  @Output() selected = new EventEmitter<SelectedSuggestion>();

  public loading = false;
  public error = false;
  public suggestions: Array<Suggestion> = [];
  public filteredSuggestions: Array<Suggestion> = [];

  public minObservers = 0;
  public minObserversGem = 0;
  public hasObserverGem: string = 'no_hr';

  public filterDate = false;
  public filterLocation = false;
  public filterStuff = false;

  public rowsPerPage = ROWS_PER_PAGE;

  constructor(
    private api: ApiService,
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.visible === true && this.order?.id && this.suggestions.length === 0) {
      this.loadMatchings();
    }
    if (this.visible === false) {
      this.suggestions = [];
    }
    if (changes.movingDate || changes.movingLocation) {
      this.onFiltersUpdated();
    }
  }

  public onFiltersUpdated(): void {
    let actualDay = null;
    let actualLocation = null;
    if (this.filterDate === true) {
      actualDay = moment((this.movingDate !== null) ? this.movingDate : this.order.start_time).format('YYYY-MM-DD');
    }
    if (this.filterLocation === true) {
      actualLocation = (this.movingLocation !== null) ? this.movingLocation : this.order.location;
    }
    this.filteredSuggestions = this.suggestions.filter((suggestion: Suggestion) => {
      return (actualDay === null || suggestion.date_as_string === actualDay) &&
        (actualLocation === null || suggestion.location.id === actualLocation.id);
    });
  }

  public needsGem(): boolean {
    return this.hasObserverGem === 'hr_mandatory' && this.minObserversGem > 0;
  }

  public selectSuggestion(suggestion: Suggestion): void {
    const selected: SelectedSuggestion = {
      date: suggestion.date,
      location: suggestion.location,
    };
    // reduce observers with gem observers
    if (this.needsGem() === true) {
      suggestion.observer = suggestion.observer.filter((observer: UserModel) => {
        return !suggestion.observer_gem.find((gem: UserModel) => {
          return gem.id === observer.id;
        });
      });
    }
    // set moderators
    if (suggestion.moderator.length > 0) {
      selected.moderator = this.getRandomUser(suggestion.moderator, 'moderator')[0];
    } else if (suggestion.moderator_external.length > 0) {
      selected.moderator = this.getRandomUser(suggestion.moderator_external, 'moderator_external')[0];
    }
    // set observers
    selected.observers = this.getRandomUser(suggestion.observer, 'observer', this.minObservers);
    if (this.needsGem() === true) {
      selected.observers_gem = this.getRandomUser(suggestion.observer_gem, 'observer_gem');
    }
    // set rooms
    if (suggestion.location.virtual !== true && suggestion.location.room_count > 0) {
      // sort on capacity
      const rooms = suggestion.location?.rooms?.sort((a: RoomModel, b: RoomModel) => {
        return b.capacity - a.capacity;
      });
      // set main if available
      const main = rooms.find((room: RoomModel) => {
        return room.type === 'main';
      }) || null;
      if (main !== null) {
        selected.room1 = main;
      }
      // set preparation if available
      const preparation = rooms.find((room: RoomModel) => {
        return room.type === 'preparation';
      }) || null;
      if (preparation !== null) {
        selected.room2 = preparation;
      }
    }
    this.selected.emit(selected);
  }

  private getRandomUser(users: Array<UserModel>, roleName: string, amount = 1): Array<UserModel> {
    const source = [...users];
    const result: Array<UserModel> = [];
    for (let i = 0; i < amount; i++) {
      if (source.length > 0) {
        const randomIndex = Math.floor(Math.random() * source.length);
        const selectedUser: any = {...source.splice(randomIndex, 1)[0]};
        selectedUser.roles = this.options.roles.filter((role: any) => {
          return role.name === roleName;
        });
        delete selectedUser.fullname;
        result.push(new UserModel().deserialize(selectedUser));
      } else {
        break;
      }
    }
    return result;
  }

  private loadMatchings(): void {
    this.loading = true;
    this.api.getOrderMovingMatchings(this.order.id).subscribe({
      next: (result: any) => {
        this.buildSuggestions(result.data);
      },
      error: () => {

      }
    }).add(() => {
      this.loading = false;
    });
  }

  private buildSuggestions(data: MatchingData): void {
    const template = this.order?.template || null;
    this.minObservers = data.settings?.min_observers || 0;
    this.minObserversGem = data.settings?.min_observers_gem || 0;
    this.hasObserverGem = data.settings?.has_observer_gem || 'no_hr';
    const roles = this.options?.roles?.filter((role: any) => {
      return ['moderator', 'moderator_external', 'observer', 'observer_gem'].includes(role.name);
    });
    let suggestions: Array<Suggestion> = [];
    if (data.users) {
      data.users.forEach((user: MatchingUser) => {
        user.appointments.forEach((appointment: MatchingAppointment) => {
          // set timings
          let start = null;
          let end = null;
          const timings = {
            start: moment(appointment.day).set({ hour: parseInt(data.settings?.day_start, 10), minute: 0, second: 0, millisecond: 0 }),
            center: moment(appointment.day).set({ hour: parseInt(data.settings?.day_center, 10), minute: parseInt(data.settings?.day_center_minutes, 10), second: 0, millisecond: 0 }),
            end: moment(appointment.day).set({ hour: parseInt(data.settings?.day_end, 10), minute: 0, second: 0, millisecond: 0 }),
          };
          if (appointment.data?.['type'] === AppointmentDataDaytype.Morning) {
            start = timings.start;
            end = timings.center;
          } else if (appointment.data?.['type'] === AppointmentDataDaytype.Afternoon) {
            start = timings.center;
            end = timings.end;
          } else {
            start = timings.start;
            end = timings.end;
          }
          let freeRanges: Array<DateRange> = [];
          const overallRange = range(start, end);
          if (Array.isArray(appointment.blocked) && appointment.blocked.length > 0) {
            const blockedRanges = appointment.blocked
              .map((period: TimePeriod) => range(moment(period.starts_at), moment(period.ends_at)))
              .sort((a: DateRange, b: DateRange) => a.start.valueOf() - b.start.valueOf());
            let currentStart = start;
            blockedRanges.forEach((blockedRange: DateRange) => {
              if (currentStart.isBefore(blockedRange.start)) {
                freeRanges.push(range(currentStart, blockedRange.start));
              }
              currentStart = blockedRange.end;
            });
            if (currentStart.isBefore(end)) {
              freeRanges.push(range(currentStart, end));
            }
          } else {
            freeRanges.push(overallRange);
          }
          const duration = template?.duration || MIN_DURATION;
          freeRanges = freeRanges.filter((freeRange: DateRange) => {
            return freeRange.end.diff(freeRange.start, 'minutes') >= duration;
          });
          // add data
          if (freeRanges.length > 0) {
            // get day from appointment
            const day = appointment.day;
            // get locations from user and filter after locations in appointment if present
            const locationIds = user.locations.filter((locationId: number) => {
              return !appointment.data?.locations || appointment.data?.locations.includes(locationId);
            });
            // iterate over locations
            this.locations.forEach((location: LocationModel) => {
              if (locationIds.includes(location.id)) {
                // get suggestion entry or add if not present
                let suggestion = suggestions.find((actual: Suggestion) => {
                  return actual.date_as_string === day && actual.location?.id === location.id;
                }) || null;
                if (suggestion === null) {
                  suggestion = {
                    date: new Date(day),
                    date_as_string: day,
                    location,
                    moderator: [],
                    moderator_external: [],
                    observer: [],
                    observer_gem: [],
                  };
                  suggestions.push(suggestion);
                }
                // add users with roles to entry
                roles.forEach((role: any) => {
                  if (user.roles.includes(role.id)) {
                    suggestion[role.name].push({
                      id: user.id,
                      firstname: user.firstname,
                      lastname: user.lastname,
                      fullname: user.firstname + ' ' + user.lastname,
                      email: user.email,
                    });
                  }
                });
              }
            });
          }
        });
      });
    }
    // filter on room availability
    if (this.locations) {
      suggestions = suggestions.filter((suggestion: Suggestion) => {
        const location = this.locations.find((location: LocationModel) => {
          return location.id === suggestion.location.id;
        }) || null;
        if (location !== null) {
          if (location.virtual === true || location.room_count === 0) {
            return true;
          }
          return location.rooms.filter((room: any) => {
            return !room.blocked_days.includes(suggestion.date_as_string);
          }).length > 0;
        }
        return false;
      });
    }
    // filter on minimum requirements (mod/obs)
    this.suggestions = suggestions.filter((suggestion: Suggestion) => {
      const hasModAndObs = (suggestion.moderator.length + suggestion.moderator_external.length) > 0 &&
        suggestion.observer.length >= this.minObservers;
      
      // Apply GEM filter only if hr_mandatory
      if (this.hasObserverGem === 'hr_mandatory') {
        return hasModAndObs && suggestion.observer_gem.length >= this.minObserversGem;
      }
      
      return hasModAndObs;
    });
    this.onFiltersUpdated();
  }

}
